import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from "react";
import { actions } from "../../../../Redux/store";
import { useMixpanel } from "../../../../Context/MixpanelContext/MixpanelProvider";

// Icon Imports
import databasePersonIcon from "../../../../assets/imgs/database-person.svg";
import latestNewsIcon from "../../../../assets/imgs/latest-news.svg";
import outlooksIcon from "../../../../assets/imgs/outlooks.svg";
import playerAnalysisIcon from "../../../../assets/imgs/player-analysis.svg";
import recapsIcon from "../../../../assets/imgs/recaps.svg";
import sitStartIcon from "../../../../assets/imgs/sit-start.svg";
import statPullingIcon from "../../../../assets/imgs/stat-pulling.svg";
import tradeAnalysisIcon from "../../../../assets/imgs/trade-analysis.svg";
import waiverWireIcon from "../../../../assets/imgs/waiver-wire.svg";
import weeklyOutlookIcon from "../../../../assets/imgs/weekly-outlook.svg";
import leagueScrollIcon from "../../../../assets/imgs/league-scroll-icon.svg";
import { useLeagueStore } from "../../../../zustandStore/leagueStore";

interface DataItem {
  id: number | string;
  chatbot_suggestion: string;
  related_route: string;
  icon_name: string;
}

interface Headline {
  text: string;
  icon: string;
  id?: number | string;
}

interface ScrollLineProps {
  headlines: Headline[];
  speed: number;
  lineNumber: number;
  direction: "left" | "right";
  onSuggestionClick: (suggestion: string) => void;
  resetKey: number;
}

const ScrollLine: React.FC<ScrollLineProps> = ({
  headlines,
  speed,
  lineNumber,
  direction,
  onSuggestionClick,
  resetKey,
}) => {
  const [isPaused, setIsPaused] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Reset animation when component remounts or resetKey changes
  useEffect(() => {
    const resetAnimation = () => {
      const container = containerRef.current;
      if (container) {
        container.style.animation = "none";
        void container.offsetHeight; // Trigger reflow
        container.style.animation = "";
      }
    };

    resetAnimation();

    // Reset animation on window focus
    const handleFocus = () => {
      resetAnimation();
    };

    window.addEventListener("focus", handleFocus);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      window.removeEventListener("focus", handleFocus);
    };
  }, [resetKey]);

  const handleClick = useCallback(
    (headlineText: string) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setIsPaused(true);
      timeoutRef.current = setTimeout(() => {
        setIsPaused(false);
      }, 1000);
      onSuggestionClick(headlineText);
    },
    [onSuggestionClick],
  );

  const animationStyle = useMemo(
    () => `
    @keyframes scroll-${lineNumber} {
      0% { transform: translateX(0%); }
      100% { transform: translateX(-50%); }
    }
    .scrolling-container-${lineNumber} {
      animation: scroll-${lineNumber} ${speed}s linear infinite;
      will-change: transform;
    }
    .scrolling-container-${lineNumber}.paused {
      animation-play-state: paused;
    }
  `,
    [lineNumber, speed],
  );

  return (
    <div className="overflow-hidden mb-2">
      <style>{animationStyle}</style>
      <div
        ref={containerRef}
        className={`flex space-x-3 scrolling-container-${lineNumber} ${isPaused ? "paused" : ""}`}
        style={{
          width: "fit-content",
          direction: "ltr",
          transform: "translateZ(0)", // Enable hardware acceleration
        }}
      >
        {[...headlines, ...headlines].map((headline, index) => (
          <button
            key={`${lineNumber}-${index}-${resetKey}`}
            onClick={() => handleClick(headline.text)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-[8px]"
            style={{
              background: "linear-gradient(90deg, #1A1F2B, #1E293B)",
              direction: "ltr",
            }}
          >
            <img
              className="w-[20px] h-[20px] object-contain"
              alt="icon"
              src={headline.icon}
            />
            <span className="text-white whitespace-nowrap text-[12px] pr-4">
              {headline.text}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

// Icon Map
const iconMap: Record<string, string> = {
  "latest-news": latestNewsIcon,
  outlooks: outlooksIcon,
  "player-analysis": playerAnalysisIcon,
  recaps: recapsIcon,
  "sit-start": sitStartIcon,
  "stat-pulling": statPullingIcon,
  "trade-analysis": tradeAnalysisIcon,
  "waiver-wire": waiverWireIcon,
  "weekly-outlook": weeklyOutlookIcon,
  "league": leagueScrollIcon,
};

const MultiLineScroll: React.FC<{ dataItems: DataItem[] }> = ({
  dataItems,
}) => {
  const [resetKey, setResetKey] = useState(() => Date.now());
  const Mixpanel = useMixpanel();
  
  const { selectedLeague } = useLeagueStore();
  useEffect(() => {
    // Reset animations when data changes
    setResetKey(Date.now());
  }, [dataItems, selectedLeague]);

  useEffect(() => {
    // Force reset when component becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        setResetKey(Date.now());
      }
    };

    // Reset on route changes
    const handleRouteChange = () => {
      setResetKey(Date.now());
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("popstate", handleRouteChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("popstate", handleRouteChange);
    };
  }, []);

  const [selectedSuggestion, setSelectedSuggestion] = useState<string>("");
  console.log(selectedSuggestion, "selectedSuggestion 111");

  const handleSuggestionClick = useCallback((suggestion: string) => {
    Mixpanel.trackEvent("select_scrolling_suggestion", {
      name: "Selected a chatbot scrolling suggestion",
      chatbotQuery: suggestion,
    });
    setSelectedSuggestion(suggestion);
    actions.modal.openChatbotSearchModal({ suggestion: suggestion });
  }, []);

  const headlines = useMemo(() => {
    if (!dataItems.length) return { line1: [], line2: [], line3: [] };

    const convertedHeadlines = dataItems.map((item) => ({
      text: item.chatbot_suggestion,
      icon: iconMap[item.icon_name] || databasePersonIcon, // Use databasePerson icon as fallback
      id: item.id, // Include the id for conditional logic
    }));

    // Separate items based on id and selectedLeague condition
    const l1Items = selectedLeague ? convertedHeadlines.filter(item => String(item.id) === "L1") : [];
    const l2Items = selectedLeague ? convertedHeadlines.filter(item => String(item.id) === "L2") : [];
    const l3Items = selectedLeague ? convertedHeadlines.filter(item => String(item.id) === "L3") : [];
    
    // Get remaining items (not L1, L2, L3) for regular distribution
    const regularItems = convertedHeadlines.filter(item => 
      !["L1", "L2", "L3"].includes(String(item.id))
    );

    const lineLength = Math.ceil(regularItems.length / 3);
    
    // Helper function to insert items at random positions
    const insertAtRandomPosition = (baseItems: any[], itemsToInsert: any[]) => {
      if (itemsToInsert.length === 0) return baseItems;
      
      const result = [...baseItems];
      itemsToInsert.forEach(item => {
        const randomIndex = Math.floor(Math.random() * (result.length + 1));
        result.splice(randomIndex, 0, item);
      });
      return result;
    };

    return {
      line1: insertAtRandomPosition(regularItems.slice(0, lineLength), l1Items),
      line2: insertAtRandomPosition(regularItems.slice(lineLength, lineLength * 2), l2Items),
      line3: insertAtRandomPosition(regularItems.slice(lineLength * 2), l3Items),
    };
  }, [dataItems, selectedLeague]);

  if (!dataItems.length) return null;

  return (
    <div className="w-full demo111 relative">
      {/* Left side gradient overlay */}
      <div 
        className="absolute left-0 top-0 bottom-0 w-12 z-10 pointer-events-none"
        style={{
          background: 'linear-gradient(89.66deg, #000000 -6.56%, rgba(9, 13, 32, 0) 99.7%)'
        }}
      />
      
      {/* Right side gradient overlay */}
      <div 
        className="absolute right-0 top-0 bottom-0 w-12 z-10 pointer-events-none"
        style={{
          background: 'linear-gradient(-89.66deg, #000000 -6.56%, rgba(9, 13, 32, 0) 99.7%)'
        }}
      />
      
      {headlines.line1.length > 0 && (
        <ScrollLine
          headlines={headlines.line1}
          speed={70}
          lineNumber={1}
          direction="right"
          onSuggestionClick={handleSuggestionClick}
          resetKey={resetKey}
        />
      )}
      {headlines.line2.length > 0 && (
        <ScrollLine
          headlines={headlines.line2}
          speed={70}
          lineNumber={2}
          direction="right"
          onSuggestionClick={handleSuggestionClick}
          resetKey={resetKey}
        />
      )}
      {headlines.line3.length > 0 && (
        <ScrollLine
          headlines={headlines.line3}
          speed={70}
          lineNumber={3}
          direction="right"
          onSuggestionClick={handleSuggestionClick}
          resetKey={resetKey}
        />
      )}
    </div>
  );
};

export default MultiLineScroll;
